import { CSRF } from '../utils/CSRF'
import { BaseRepository } from './BaseRepository'

export type FeatureSet = {
  api_access: boolean
  billing_rate: number | string
  custom_voice_limit: number
  localized_languages: string[]
  platform_permissions: {
    sts: boolean
    tts: boolean
    fill: boolean
    detect: boolean
    live_sts: boolean
  }
  monthly_synthesis_credits: number | string
  voice_cloning_permissions: {
    api_access: boolean
    data_upload: boolean
    consent_required: boolean
    rapid_voice_limit: number
    web_recorder_access: boolean
    professional_voice_limit: number
  }
}

export type Plan = {
  id: number
  stripe_name: string
  name: string
  limit: number
  seats: number
  rate: number
  subscription_fee: string
  active: boolean
  fee_per_voice: number
  payment_link: string | null
  features: string[]
  description: string
  order: number
  featured: boolean
  feature_set: FeatureSet
}

export type VoiceCount = {
  count: number
  left: number
}

export type StripeCoupon = {
  id: string
  name: string | null
  percent_off?: number | null
  amount_off?: number | null
}

export type Promo = {
  id: string
  name: string
  coupon: StripeCoupon
}

export type BillingData = {
  billing_portal_url: string | null
  subscription_end_date: string | null
  subscription_cancel_at: string | null
  subscription_cancel_at_period_end: boolean
  usage: number
  usage_units: string
  plans: Plan[]
  current_plan: Plan | null
  is_paid_user: boolean
  promo: Promo | null
  prorated_upgrade_eligible: boolean
  pro_referral_discount_rate: number
  upgrade_credit: number
  discount_type: string | null
  is_admin: boolean
  professional_voices: VoiceCount
  rapid_voices: VoiceCount
}

export type CreditOption = {
  checkout_url: string | null
  price: string
  formatted_credits: string
  credits: number
}

export class BillingRepository extends BaseRepository {
  getBillingData() {
    return super.getHttpClientInstance().get<BillingData>('/account/billing.json', {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  getCreditsUsage(compute: boolean = false) {
    return super.getHttpClientInstance().get<{
      is_over_limit: boolean
      current_usage: number
      max_usage: number
      billing_model: 'characters' | 'seconds' | 'wallet'
      plan_has_limit: boolean
      is_payg: boolean
      is_custom_plan: boolean
      payg_credits?: number
      uses_billing_engine: boolean
      product_limits?: Record<string, { is_over_limit: boolean }>
      wallet_balance_cents?: number
      wallet_balance_dollars?: string
      wallet_bar_max_cents?: number
    }>('/account/limit_reached', {
      params: { compute },
      headers: {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      },
    })
  }

  getCreditOptions() {
    return super.getHttpClientInstance().get<CreditOption[]>('/account/credit_options.json', {
      headers: {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      },
    })
  }
}
